import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import axios from 'axios';
import { Bot, Save, ToggleLeft, ToggleRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminChatbotConfig() {
  const [config, setConfig] = useState({ ai_enabled: true, system_prompt: '', model: 'gpt-4.1-mini' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/chatbot/config`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/chatbot/config`, config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {} finally { setSaving(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 14px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'var(--font-body)' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="chatbot" />
      <main data-testid="admin-chatbot-config" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32 }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <Bot size={28} style={{ color: 'var(--primary)' }} />
            <div>
              <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>AI Chatbot Configuration</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Configure the AI assistant for live chat</p>
            </div>
          </div>

          {saved && (
            <div style={{ padding: '12px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid #16A34A', color: '#16A34A', marginBottom: 20, fontSize: 14, fontWeight: 500 }}>
              Configuration saved successfully!
            </div>
          )}

          <div style={{ background: 'white', border: '1px solid var(--border)', padding: 32 }}>
            {/* AI Toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, padding: 20, background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>AI Chatbot</p>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Enable GPT-powered automatic responses to customer questions</p>
              </div>
              <button data-testid="ai-toggle" onClick={() => setConfig({...config, ai_enabled: !config.ai_enabled})}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                {config.ai_enabled ? (
                  <><ToggleRight size={36} style={{ color: '#16A34A' }} /> <span style={{ fontWeight: 700, color: '#16A34A', fontSize: 14 }}>ON</span></>
                ) : (
                  <><ToggleLeft size={36} style={{ color: 'var(--text-muted)' }} /> <span style={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: 14 }}>OFF</span></>
                )}
              </button>
            </div>

            {/* Model */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>AI Model</label>
              <select data-testid="ai-model-select" value={config.model} onChange={e => setConfig({...config, model: e.target.value})}
                style={{...inputStyle, cursor: 'pointer'}}>
                <option value="gpt-4.1-mini">GPT-4.1 Mini (Fast, Cost-effective)</option>
                <option value="gpt-4.1">GPT-4.1 (Balanced)</option>
                <option value="gpt-5.1">GPT-5.1 (Advanced)</option>
              </select>
            </div>

            {/* System Prompt */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>System Prompt (AI Instructions)</label>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Define how the AI assistant should behave, what information it knows, and how it responds to customers.</p>
              <textarea data-testid="system-prompt" value={config.system_prompt} onChange={e => setConfig({...config, system_prompt: e.target.value})}
                rows={18} style={{...inputStyle, resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6}} />
            </div>

            {/* Save */}
            <button data-testid="save-chatbot-config" onClick={handleSave} disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 28px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: saving ? 0.6 : 1 }}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
